import { createFilterOptions } from "@mui/material";
import { ProductFeature } from "../../../api/featureRequests";
import DSAutocomplete from "../../system-design/DSAutocomplete";
import DSTextField from "../../system-design/DSTextField";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";

// Either an existing feature or a title the admin typed to create a new one.
export type FeatureChoice =
  | { kind: "existing"; feature: ProductFeature }
  | { kind: "new"; title: string };

type Option = { id: string; title: string; feature?: ProductFeature };

const NEW_OPTION_ID = "__new__";
const filter = createFilterOptions<Option>({
  stringify: (option) => option.title,
});

const FeatureAutocomplete = (props: {
  features: ProductFeature[];
  value: FeatureChoice | null;
  onChange: (value: FeatureChoice | null) => void;
  allowCreate?: boolean;
  excludeId?: string;
  label?: string;
  autoFocus?: boolean;
  size?: "small" | "medium";
}) => {
  const options: Option[] = props.features
    .filter((feature) => feature.id !== props.excludeId)
    .map((feature) => ({ id: feature.id, title: feature.title, feature }));

  const value: Option | null =
    props.value?.kind === "existing"
      ? {
          id: props.value.feature.id,
          title: props.value.feature.title,
          feature: props.value.feature,
        }
      : props.value?.kind === "new"
        ? { id: NEW_OPTION_ID, title: props.value.title }
        : null;

  return (
    <DSAutocomplete<Option>
      options={options}
      value={value}
      isOptionEqualToValue={(a, b) => a.id === b.id && a.title === b.title}
      getOptionLabel={(option) => option.title}
      filterOptions={(opts, state) => {
        const filtered = filter(opts, state);
        const typed = state.inputValue.trim();
        const exists = opts.some(
          (o) => o.title.toLowerCase() === typed.toLowerCase(),
        );
        if (props.allowCreate && typed && !exists) {
          filtered.push({ id: NEW_OPTION_ID, title: typed });
        }
        return filtered;
      }}
      onChange={(_, option) => {
        if (!option) props.onChange(null);
        else if (option.feature)
          props.onChange({ kind: "existing", feature: option.feature });
        else props.onChange({ kind: "new", title: option.title });
      }}
      renderOption={(liProps, option) => (
        <li {...liProps} key={`${option.id}:${option.title}`}>
          {option.feature ? (
            <DSBox
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                gap: 2,
              }}
            >
              <span>{option.title}</span>
              <DSTypography sx={{ fontSize: 12, color: "text.secondary" }}>
                {option.feature.customerCount} customer
                {option.feature.customerCount === 1 ? "" : "s"}
              </DSTypography>
            </DSBox>
          ) : (
            <DSTypography variant="body2" sx={{ fontWeight: 600 }}>
              + Create new feature: &quot;{option.title}&quot;
            </DSTypography>
          )}
        </li>
      )}
      renderInput={(params) => (
        <DSTextField
          {...params}
          label={props.label ?? "Feature"}
          placeholder={
            props.allowCreate
              ? "Search existing features or type a new one"
              : "Search features"
          }
          autoFocus={props.autoFocus}
          size={props.size}
        />
      )}
    />
  );
};

export default FeatureAutocomplete;
